import { CLIENT_STATUSES, type ClientStatus } from '@glory/clients/domain';
import { t } from '@glory/i18n';
import {
  Button,
  Callout,
  cn,
  Dialog,
  EmptyState,
  InfoHint,
  PageHeader,
  Segments,
  TextInput,
} from '@glory/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search, Users } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { useEffect, useState } from 'react';

import { parseClientsSearch } from '../../../features/clients/model/filters.ts';
import { statusLabel } from '../../../features/clients/model/labels.ts';
import { ClientPanel } from '../../../features/clients/ui/client-panel.tsx';
import { ClientTable } from '../../../features/clients/ui/client-table.tsx';
import { MergeDialog } from '../../../features/clients/ui/merge-dialog.tsx';
import { NewClientDialog } from '../../../features/clients/ui/new-client-dialog.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute, usePermissions } from '../../../lib/permissions.ts';
import { useDebouncedValue } from '../../../lib/use-debounced-value.ts';

export const Route = createFileRoute('/_app/clients/')({
  validateSearch: (search: Record<string, unknown>) => parseClientsSearch(search),
  beforeLoad: requirePermissionRoute('sales.use'),
  component: ClientsPage,
});

const STATUS_FILTERS: readonly (ClientStatus | undefined)[] = [undefined, ...CLIENT_STATUSES];

/** Список клиентов с панелью справа: фильтры «Мои / Все» и статус, поиск, «Новый клиент». */
function ClientsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canMerge = usePermissions().can('clients.merge');
  const [typed, setTyped] = useState(search.q ?? '');
  const query = useDebouncedValue(typed.trim(), 250);
  const filtered = search.scope !== undefined || search.status !== undefined || query !== '';

  // Поиск живёт в адресе: набранное попадает в ссылку, когда ввод затих.
  useEffect(() => {
    if ((search.q ?? '') === query) return;
    void navigate({ search: (prev) => ({ ...prev, q: query === '' ? undefined : query }), replace: true });
  }, [query, search.q, navigate]);

  const list = useQuery({
    ...orpc.clients.list.queryOptions({
      input: {
        ...(search.scope === undefined ? {} : { scope: search.scope }),
        ...(search.status === undefined ? {} : { status: search.status }),
        ...(query === '' ? {} : { query }),
      },
    }),
    placeholderData: keepPreviousData,
  });
  const items = list.data?.items ?? [];
  const openIndex = items.findIndex((client) => client.id === search.client);

  /** Открыть карточку — новая запись истории (назад закроет её); листать и закрыть — без записи. */
  const show = (id: string | undefined, replace: boolean) => {
    void navigate({ search: (prev) => ({ ...prev, client: id }), replace });
  };
  const setFilter = (patch: { scope?: 'mine' | undefined; status?: ClientStatus | undefined }) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };
  const resetFilters = () => {
    setTyped('');
    void navigate({ search: (prev) => ({ client: prev.client }), replace: true });
  };
  const newClient = (
    <Button
      variant="primary"
      onClick={() => {
        void navigate({ search: (prev) => ({ ...prev, create: true }) });
      }}
    >
      <Plus /> {t('clients.newClient.title')}
    </Button>
  );

  return (
    <div className="relative flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t('clients.title')}
            <InfoHint hint={{ text: t('clients.hint'), example: t('clients.hintExample') }} />
          </span>
        }
        actions={newClient}
      />
      <p className="-mt-2 max-w-[70ch] text-muted">{t('clients.lead')}</p>

      <div className="flex flex-wrap items-center gap-2">
        <Segments
          size="sm"
          value={search.scope ?? 'all'}
          options={[
            { value: 'all', label: t('clients.scopeAll') },
            { value: 'mine', label: t('clients.scopeMine') },
          ]}
          onValueChange={(scope) => {
            setFilter({ scope: scope === 'mine' ? 'mine' : undefined });
          }}
        />
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('clients.columnStatus')}>
          {STATUS_FILTERS.map((status) => {
            const active = search.status === status;
            return (
              <button
                key={status ?? 'open'}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setFilter({ status });
                }}
                className={cn(
                  'rounded-full border-thick px-2.5 py-0.5 text-small font-semibold transition-colors',
                  active
                    ? 'border-strong bg-strong text-on-strong'
                    : 'border-border text-muted hover:text-text',
                )}
              >
                {status === undefined ? t('clients.statusOpen') : statusLabel(status)}
              </button>
            );
          })}
        </div>
        <TextInput
          aria-label={t('common.search')}
          placeholder={t('clients.searchPlaceholder')}
          leading={<Search className="size-4" aria-hidden="true" />}
          boxClassName="ml-auto w-full sm:w-72"
          value={typed}
          onValueChange={setTyped}
        />
      </div>

      {list.isError ? (
        <Callout className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1">{list.error.message}</span>
          <Button
            size="sm"
            onClick={() => {
              void list.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </Callout>
      ) : (
        <>
          <ClientTable
            items={items}
            loading={list.isPending}
            activeId={search.client ?? null}
            onOpen={(client) => {
              show(client.id, search.client !== undefined);
            }}
            empty={
              filtered ? (
                <EmptyState
                  icon={<Users />}
                  title={t('clients.emptyFilteredTitle')}
                  description={t('clients.emptyFilteredText')}
                  action={<Button onClick={resetFilters}>{t('clients.resetFilters')}</Button>}
                />
              ) : (
                <EmptyState
                  icon={<Users />}
                  title={t('clients.emptyTitle')}
                  description={t('clients.emptyText')}
                  action={newClient}
                />
              )
            }
          />
          {list.data && list.data.total > items.length ? (
            <p className="text-small text-muted">
              {t('clients.moreHint', { count: items.length, total: list.data.total })}
            </p>
          ) : null}
        </>
      )}

      {/* Постоянный key: переход к соседней записи не закрывает и не открывает панель заново. */}
      <AnimatePresence initial={false}>
        {search.client ? (
          <ClientPanel
            key="panel"
            id={search.client}
            onClose={() => {
              show(undefined, true);
            }}
            onPrevious={() => {
              const previous = items[openIndex - 1];
              if (previous) show(previous.id, true);
            }}
            onNext={() => {
              const next = items[openIndex + 1];
              if (next) show(next.id, true);
            }}
            onExpand={() => {
              void navigate({ to: '/clients/$id', params: { id: search.client ?? '' } });
            }}
          />
        ) : null}
      </AnimatePresence>

      <Dialog
        open={search.create === true}
        onOpenChange={(open) => {
          if (open) return;
          void navigate({ search: (prev) => ({ ...prev, create: undefined }), replace: true });
        }}
      >
        {search.create ? (
          <NewClientDialog
            onDone={(id) => {
              void navigate({ search: (prev) => ({ ...prev, create: undefined, client: id }) });
            }}
          />
        ) : null}
      </Dialog>

      {/* «Объединить клиентов» из ⌘K: обе стороны выбираются в окне; после — карточка основного. */}
      <Dialog
        open={search.merge === true && canMerge}
        onOpenChange={(open) => {
          if (open) return;
          void navigate({ search: (prev) => ({ ...prev, merge: undefined }), replace: true });
        }}
      >
        {search.merge === true && canMerge ? (
          <MergeDialog
            primaryId={null}
            duplicateId={null}
            onDone={(primary) => {
              void navigate({ search: (prev) => ({ ...prev, merge: undefined, client: primary.id }) });
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}
